import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { useAppSession } from './useAppSession';
import { useAuth } from '../lib/auth';
import { integrationsApi } from '../lib/api';
// Imported from the dedicated module, NOT the '../lib/api' barrel: tests mock
// the barrel, so a barrel import would make ApiError `undefined` and
// `x instanceof undefined` throw. apiError.ts is standalone (no import.meta.env).
import { ApiError } from '../lib/api/apiError';
import { capture } from '../lib/analytics';
import { log } from '@/lib/logger';

log.info('[useIntegrations] Module loaded');

/**
 * Hook to fetch the list of connected integrations
 */
export function useIntegrations() {
  log.info('[useIntegrations] useIntegrations called');

  return useApiQuery({
    queryKey: ['integrations'],
    queryFn: async (token) => {
      log.info('[useIntegrations] Fetching integrations...');
      const data = await integrationsApi.list(token);
      log.info(`[useIntegrations] Fetched ${data.length} integrations`);
      return data;
    },
    staleTime: 30 * 1000, // Cache for 30 seconds
  });
}

/**
 * Get a specific integration by provider
 */
export function useIntegration(provider: 'jira' | 'linear' | 'notion' | 'asana' | 'trello' | 'clickup' | 'github' | 'basecamp' | 'monday') {
  const { data: integrations, isLoading, error, refetch } = useIntegrations();
  log.info('[useIntegrations] useIntegration called for provider:', { provider });

  const integration = integrations?.find((i) => i.provider === provider);
  // `null`: the list read failed and nothing is cached, so the card must not
  // claim "not connected" (2026-09-16 integrations sweep, INT-03: a 503 on the
  // list rendered five ordinary Connect buttons). A refetch that fails over
  // cached data keeps reporting the last successful read.
  const isConnected: boolean | null = integrations ? !!integration : error ? null : false;

  return {
    integration,
    isConnected,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Shown when the OAuth popup closed but no connection was ever stored, which is
 * what a provider-side rejection looks like from our side. Deliberately does not
 * blame the user: the most common cause we have actually seen was our own broken
 * app registration (see `closed_before_callback` below).
 */
export const CONNECT_CLOSED_BEFORE_CALLBACK_MESSAGE =
  "Connection wasn't completed. If the provider showed an error, that's the reason. Try again.";

/**
 * Is this a HANDLED connect outcome rather than a crash? A user who closes the
 * popup, or a provider that refuses before redirecting, is an expected result we
 * already surface on the card and count via `integration_connect_failed`.
 *
 * This matters because the dashboard captures console errors as PostHog
 * `$exception`s (`capture_exceptions` in posthogConfig). Logging this at error
 * level would file a fake crash for every abandoned OAuth popup, which is the
 * exact noise removed by the 2026-07-16 handled-4xx fix. Mirrors
 * `isHandledHttpStatus` in lib/api/errorLogLevel.ts.
 */
export function isHandledConnectOutcome(error: unknown): boolean {
  return error instanceof Error && error.message === CONNECT_CLOSED_BEFORE_CALLBACK_MESSAGE;
}

/** Log an OAuth connect failure at the level its severity actually warrants. */
function logConnectFailure(provider: string, error: unknown): void {
  if (isHandledConnectOutcome(error)) {
    log.warn(`[useIntegrations] ${provider} connect closed before callback`, { provider });
    return;
  }
  // An ApiError was thrown by fetchApi (e.g. a 500 CONFIG_ERROR from
  // /integrations/<provider>/auth when the provider's OAuth credentials aren't
  // deployed) — and fetchApi ALREADY logged it (at error for a 5xx/unhandled-4xx,
  // at warn for a handled 401/403/404). Re-logging here at error files a SECOND
  // $exception for one failure (the dashboard captures console.error via
  // capture_exceptions), doubling Error Tracking impact counts and splitting one
  // defect across two issues. Downgrade to warn: fetchApi owns the record.
  // Mirrors isHandledHttpStatus (errorLogLevel.ts) / isHandledConnectOutcome.
  if (error instanceof ApiError) {
    log.warn(`[useIntegrations] ${provider} connect failed (already logged by fetchApi)`, {
      provider,
      status: error.status,
      code: error.code,
    });
    return;
  }
  // Failures thrown OUTSIDE fetchApi (popup blocked, postMessage, "Not
  // authenticated", OAuth timeout) were never logged — keep them at error level.
  log.error(`[useIntegrations] ${provider} OAuth failed:`, { error });
}

/**
 * Did the connection actually land? Read server-side after the popup closes.
 * Slack keeps its own status rather than an `integrations` row, so it needs a
 * different read; every other provider shows up in `integrationsApi.list`.
 */
async function didConnectLand(provider: string, token: string, teamId?: string): Promise<boolean> {
  if (provider === 'slack') {
    const status = await integrationsApi.getSlackStatus(token, teamId);
    return !!status.connected;
  }
  const list = await integrationsApi.list(token);
  return list.some((i: { provider: string }) => i.provider === provider);
}

/**
 * Open an OAuth popup and settle when it closes. Centralises the duplicated
 * Jira/Linear/Notion/Asana/Trello/Slack popup pattern. Resolves only once the
 * connection is confirmed stored (or cannot be checked); rejects when the popup
 * closed with nothing stored.
 */
function openOAuthPopup(
  url: string,
  name: string,
  queryClient: ReturnType<typeof useQueryClient>,
  verify: { provider: string; token: string; teamId?: string },
): Promise<void> {
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;

  const popup = window.open(
    url,
    name,
    `width=${width},height=${height},left=${left},top=${top}`
  );

  if (!popup) {
    throw new Error('Popup blocked. Please allow popups for this site.');
  }

  log.info(`[useIntegrations] Opened ${name} OAuth popup`);

  return new Promise<void>((resolve, reject) => {
    // Both the close-poll and the 5-minute ceiling can fire; whichever wins
    // settles the promise once and disarms the other.
    let settled = false;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearInterval(checkInterval);
      clearTimeout(timeoutId);
      fn();
    };

    const checkInterval = setInterval(() => {
      // Read `closed` in its own try: while the popup sits on the provider's
      // origin this throws, and that is the normal in-flight state, not an error.
      let isClosed = false;
      try {
        isClosed = popup.closed;
      } catch {
        return;
      }
      if (!isClosed || settled) return;

      settled = true;
      clearInterval(checkInterval);
      clearTimeout(timeoutId);

      // Nothing between here and the verification may be allowed to throw out
      // of this callback: the timeout is already disarmed, so an escaping throw
      // would leave the promise permanently unsettled and hang the mutation.
      try {
        queryClient.invalidateQueries({ queryKey: ['integrations'] });
        log.info(`[useIntegrations] ${name} popup closed, invalidating integrations`);
      } catch (err) {
        log.warn('[useIntegrations] Post-close invalidation failed', { err });
      }

      // A closed popup is NOT proof of success. When the provider rejects us
      // at its own authorize screen (e.g. an OAuth app that is private to the
      // owning workspace) it never redirects to our callback, so
      // `IntegrationConnectedPage` never loads and never fires
      // `integration_connect_failed`. Before this check, that case resolved
      // as success and the user saw nothing at all. Verify server-side.
      // `didConnectLand` is async, so it always returns a promise and both
      // branches below settle: there is no path that leaves this pending.
      didConnectLand(verify.provider, verify.token, verify.teamId)
        .then((landed) => {
          if (landed) {
            resolve();
            return;
          }
          log.warn(`[useIntegrations] ${name} popup closed with no stored connection`);
          capture('integration_connect_failed', {
            provider: verify.provider,
            reason: 'closed_before_callback',
          });
          reject(new Error(CONNECT_CLOSED_BEFORE_CALLBACK_MESSAGE));
        })
        .catch((err) => {
          // The verification itself failed. Never turn that into a reported
          // connect failure: a false failure on a connect that actually
          // worked is worse than the silence this check exists to fix.
          log.warn('[useIntegrations] Could not verify connection after popup closed', { err });
          resolve();
        });
    }, 500);

    // Timeout after 5 minutes
    const timeoutId = setTimeout(() => {
      finish(() => {
        if (!popup.closed) {
          popup.close();
        }
        reject(new Error('OAuth timed out'));
      });
    }, 5 * 60 * 1000);
  });
}

/**
 * Hook to initiate Jira OAuth flow.
 * Uses manual auth because the popup close handler needs queryClient
 * for invalidation inside the Promise body (not just onSuccess).
 */
export function useConnectJira() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  log.info('[useIntegrations] useConnectJira called');

  return useMutation({
    mutationFn: async () => {
      log.info('[useIntegrations] Initiating Jira OAuth...');
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const { url, state } = await integrationsApi.getJiraAuthUrl(token);
      sessionStorage.setItem('jira_oauth_state', state);
      return openOAuthPopup(url, 'jira_oauth', queryClient, { provider: 'jira', token });
    },
    onSuccess: () => {
      log.info('[useIntegrations] Jira OAuth completed');
    },
    onError: (error) => {
      logConnectFailure('Jira', error);
    },
  });
}

/**
 * Hook to disconnect Jira
 */
export function useDisconnectJira() {
  log.info('[useIntegrations] useDisconnectJira called');

  return useApiMutation<unknown, void>({
    mutationFn: async (token) => {
      log.info('[useIntegrations] Disconnecting Jira...');
      await integrationsApi.disconnectJira(token);
    },
    invalidateKeys: [['integrations']],
    onSuccess: () => {
      log.info('[useIntegrations] Jira disconnected');
    },
    onError: (error) => {
      log.error('[useIntegrations] Failed to disconnect Jira:', { error });
    },
  });
}

/**
 * Hook to initiate Linear OAuth flow.
 * Uses manual auth because the popup close handler needs queryClient
 * for invalidation inside the Promise body (not just onSuccess).
 */
export function useConnectLinear() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  log.info('[useIntegrations] useConnectLinear called');

  return useMutation({
    mutationFn: async () => {
      log.info('[useIntegrations] Initiating Linear OAuth...');
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const { url, state } = await integrationsApi.getLinearAuthUrl(token);
      sessionStorage.setItem('linear_oauth_state', state);
      return openOAuthPopup(url, 'linear_oauth', queryClient, { provider: 'linear', token });
    },
    onSuccess: () => {
      log.info('[useIntegrations] Linear OAuth completed');
    },
    onError: (error) => {
      logConnectFailure('Linear', error);
    },
  });
}

/**
 * Hook to disconnect Linear
 */
export function useDisconnectLinear() {
  log.info('[useIntegrations] useDisconnectLinear called');

  return useApiMutation<unknown, void>({
    mutationFn: async (token) => {
      log.info('[useIntegrations] Disconnecting Linear...');
      await integrationsApi.disconnectLinear(token);
    },
    invalidateKeys: [['integrations']],
    onSuccess: () => {
      log.info('[useIntegrations] Linear disconnected');
    },
    onError: (error) => {
      log.error('[useIntegrations] Failed to disconnect Linear:', { error });
    },
  });
}

/**
 * Hook to initiate Notion OAuth flow.
 */
export function useConnectNotion() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  log.info('[useIntegrations] useConnectNotion called');

  return useMutation({
    mutationFn: async () => {
      log.info('[useIntegrations] Initiating Notion OAuth...');
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const { url, state } = await integrationsApi.getNotionAuthUrl(token);
      sessionStorage.setItem('notion_oauth_state', state);
      return openOAuthPopup(url, 'notion_oauth', queryClient, { provider: 'notion', token });
    },
    onSuccess: () => {
      log.info('[useIntegrations] Notion OAuth completed');
    },
    onError: (error) => {
      logConnectFailure('Notion', error);
    },
  });
}

/**
 * Hook to disconnect Notion
 */
export function useDisconnectNotion() {
  log.info('[useIntegrations] useDisconnectNotion called');

  return useApiMutation<unknown, void>({
    mutationFn: async (token) => {
      log.info('[useIntegrations] Disconnecting Notion...');
      await integrationsApi.disconnectNotion(token);
    },
    invalidateKeys: [['integrations']],
    onSuccess: () => {
      log.info('[useIntegrations] Notion disconnected');
    },
    onError: (error) => {
      log.error('[useIntegrations] Failed to disconnect Notion:', { error });
    },
  });
}

/**
 * Hook to initiate Asana OAuth flow.
 */
export function useConnectAsana(provider: 'asana' | 'clickup' | 'github' | 'basecamp' | 'monday' = 'asana') {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  log.info('[useIntegrations] useConnectAsana called');

  return useMutation({
    mutationFn: async () => {
      log.info('[useIntegrations] Initiating OAuth', { provider });
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const { url, state } = await (provider === 'github' ? integrationsApi.getGitHubAuthUrl(token) : provider === 'clickup' ? integrationsApi.getClickUpAuthUrl(token) : provider === 'basecamp' ? integrationsApi.getBasecampAuthUrl(token) : provider === 'monday' ? integrationsApi.getMondayAuthUrl(token) : integrationsApi.getAsanaAuthUrl(token));
      sessionStorage.setItem(`${provider}_oauth_state`, state);
      return openOAuthPopup(url, `${provider}_oauth`, queryClient, { provider, token });
    },
    onSuccess: () => {
      log.info('[useIntegrations] OAuth completed', { provider });
    },
    onError: (error) => {
      logConnectFailure(provider, error);
    },
  });
}

/**
 * Hook to disconnect Asana
 */
export function useDisconnectAsana(provider: 'asana' | 'clickup' | 'github' | 'basecamp' | 'monday' = 'asana') {
  log.info('[useIntegrations] useDisconnectAsana called');

  return useApiMutation<unknown, void>({
    mutationFn: async (token) => {
      log.info('[useIntegrations] Disconnecting integration', { provider });
      await (provider === 'github' ? integrationsApi.disconnectGitHub(token) : provider === 'clickup' ? integrationsApi.disconnectClickUp(token) : provider === 'basecamp' ? integrationsApi.disconnectBasecamp(token) : provider === 'monday' ? integrationsApi.disconnectMonday(token) : integrationsApi.disconnectAsana(token));
    },
    invalidateKeys: [['integrations']],
    onSuccess: () => {
      log.info('[useIntegrations] Integration disconnected', { provider });
    },
    onError: (error) => {
      log.error('[useIntegrations] Failed to disconnect integration', { provider, error });
    },
  });
}

/**
 * Hook to initiate Trello OAuth flow.
 */
export function useConnectTrello() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  log.info('[useIntegrations] useConnectTrello called');

  return useMutation({
    mutationFn: async () => {
      log.info('[useIntegrations] Initiating Trello OAuth...');
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const { url, state } = await integrationsApi.getTrelloAuthUrl(token);
      sessionStorage.setItem('trello_oauth_state', state);
      return openOAuthPopup(url, 'trello_oauth', queryClient, { provider: 'trello', token });
    },
    onSuccess: () => {
      log.info('[useIntegrations] Trello OAuth completed');
    },
    onError: (error) => {
      logConnectFailure('Trello', error);
    },
  });
}

/**
 * Hook to disconnect Trello
 */
export function useDisconnectTrello() {
  log.info('[useIntegrations] useDisconnectTrello called');

  return useApiMutation<unknown, void>({
    mutationFn: async (token) => {
      log.info('[useIntegrations] Disconnecting Trello...');
      await integrationsApi.disconnectTrello(token);
    },
    invalidateKeys: [['integrations']],
    onSuccess: () => {
      log.info('[useIntegrations] Trello disconnected');
    },
    onError: (error) => {
      log.error('[useIntegrations] Failed to disconnect Trello:', { error });
    },
  });
}

// ============================================================================
// Slack (team-scoped, separate from user-scoped integrations)
// ============================================================================

interface SlackStatus {
  connected: boolean;
  slackTeamName?: string;
  channelId?: string | null;
  channelName?: string | null;
  isActive?: boolean;
  createdAt?: string;
}

/**
 * Hook to get the team's Slack connection status
 */
export function useSlackStatus() {
  const { data: session } = useAppSession();
  const teamId = session?.activeTeamId;
  // Through useApiQuery like every other read in this file, so a missing
  // session token is an ERROR (unknown), never a successful "connected: false",
  // and a 5xx follows the shared retry policy instead of react-query's default
  // three retries. The card renders the unknown state on `status === null`.
  const { data, isLoading, error, refetch } = useApiQuery<SlackStatus>({
    queryKey: ['slack-status', teamId],
    queryFn: (token) => integrationsApi.getSlackStatus(token, teamId),
    enabled: !!teamId,
    staleTime: 30 * 1000,
  });

  // `null`: the status read failed with nothing cached (INT-03). The card
  // offers a re-read; defaulting to connected:false here is what told a saved
  // Slack connection's owner to "Connect" during an outage.
  const status: SlackStatus | null = data ?? (error ? null : ({ connected: false } as SlackStatus));
  return { status, isLoading, refetch, teamId };
}

/**
 * Hook to initiate Slack OAuth flow
 */
export function useConnectSlack() {
  const { data: session } = useAppSession();
  const teamId = session?.activeTeamId;
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      log.info('[useIntegrations] Initiating Slack OAuth...');
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      if (!teamId) throw new Error('Workspace not available. Try again.');
      const { url, state } = await integrationsApi.getSlackAuthUrl(token, teamId);
      sessionStorage.setItem('slack_oauth_state', state);
      return openOAuthPopup(url, 'slack_oauth', queryClient, { provider: 'slack', token, teamId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-status'] });
      log.info('[useIntegrations] Slack OAuth completed');
    },
    onError: (error) => {
      logConnectFailure('Slack', error);
    },
  });
}

/**
 * Hook to disconnect Slack
 */
export function useDisconnectSlack() {
  const { data: session } = useAppSession();
  const teamId = session?.activeTeamId;
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      log.info('[useIntegrations] Disconnecting Slack...');
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      if (!teamId) throw new Error('Workspace not available. Try again.');
      await integrationsApi.disconnectSlack(token, teamId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['slack-status'] });
      log.info('[useIntegrations] Slack disconnected');
    },
    onError: (error) => {
      log.error('[useIntegrations] Failed to disconnect Slack:', { error });
    },
  });
}
